import axios, { isAxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import type { SerialValidationRecord } from '@/domain/izzi';
import type { ProcessInventoryResponse } from '@/domain/inventory';
import { useHttp } from '@/hooks/http';
import { VITE_URL_SERVICE } from '@/utils/envs';

const httpPublic = axios.create({
  baseURL: VITE_URL_SERVICE,
  headers: { 'ngrok-skip-browser-warning': 'true' },
});

const RELEASED_HOURS = 168;
const DEFAULT_PAGE_SIZE = 20;

const cleanMessage = (msg: string) => msg.replace(/^(\[.*?\]\s*)+/, '').trim();

interface PageState {
  rows: SerialValidationRecord[];
  total: number;
}

const emptyPage = (): PageState => ({ rows: [], total: 0 });

// 0=Por PO | 1=Cuarentena | 2=Liberadas | 3=Errores
export type SeriesTab = 0 | 1 | 2 | 3;

export const useSeriesSection = () => {
  const http = useHttp();
  const [activeTab, setActiveTab] = useState<SeriesTab>(0);
  const [isLoading, setIsLoading] = useState(false);

  // Filtro compartido entre tabs
  const [filterInput, setFilterInput] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');

  // Paginación por tab
  const [poPage, setPoPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [quarPage, setQuarPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [relPage, setRelPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [errPage, setErrPage] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });

  // Datos por tab
  const [poData, setPoData] = useState<PageState>(emptyPage());
  const [quarData, setQuarData] = useState<PageState>(emptyPage());
  const [relData, setRelData] = useState<PageState>(emptyPage());
  const [errData, setErrData] = useState<PageState>(emptyPage());

  // Modal de detalle de error (tab 3)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedError, setSelectedError] = useState<string | null>(null);

  // ── Tab 0: Por PO ────────────────────────────────────────────────────────

  const fetchByPo = async (page: number, pageSize: number, po: string) => {
    if (!po.trim()) return;
    setIsLoading(true);
    try {
      const { data } = await httpPublic.get<{ data: SerialValidationRecord[]; total: number }>(
        '/api/v3/ops-izzi/serial-validations',
        { params: { purchaseOrder: po.trim(), page, pageSize } },
      );
      setPoData({ rows: data.data, total: data.total });
    } catch {
      toast.error('Error al cargar las series de la PO.');
    } finally {
      setIsLoading(false);
    }
  };

  const onPoPageChange = (model: { page: number; pageSize: number }) => {
    setPoPage(model);
    fetchByPo(model.page, model.pageSize, appliedFilter);
  };

  const onDelete = async (id: string) => {
    setIsLoading(true);
    try {
      await httpPublic.delete(`/api/v3/ops-izzi/serial-validations/${id}`);
      setPoData((prev) => ({
        rows: prev.rows.filter((r) => r.id !== id),
        total: Math.max(0, prev.total - 1),
      }));
      toast.success('Serie desvinculada correctamente.');
    } catch {
      toast.error('Error al desvincular la serie.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Tab 1: Cuarentena ────────────────────────────────────────────────────

  const fetchQuarantined = useCallback(
    async (page: number, pageSize: number, po?: string) => {
      setIsLoading(true);
      try {
        const result = await http.izzi.getSerials({
          status: 'QUARANTINED',
          page: page + 1,
          pageSize,
          ...(po ? { purchaseOrder: po } : {}),
        });
        setQuarData({ rows: result.data, total: result.total });
      } catch {
        setQuarData(emptyPage());
        toast.error('Error al cargar series en cuarentena.');
      } finally {
        setIsLoading(false);
      }
    },
    [http.izzi],
  );

  const onQuarPageChange = (model: { page: number; pageSize: number }) => {
    setQuarPage(model);
    fetchQuarantined(model.page, model.pageSize, appliedFilter);
  };

  // ── Tab 2: Liberadas ─────────────────────────────────────────────────────

  const fetchReleased = useCallback(
    async (page: number, pageSize: number, po?: string) => {
      setIsLoading(true);
      try {
        const result = await http.izzi.getSerials({
          status: 'RELEASED',
          hours: RELEASED_HOURS,
          page: page + 1,
          pageSize,
          ...(po ? { purchaseOrder: po } : {}),
        });
        setRelData({ rows: result.data, total: result.total });
      } catch {
        setRelData(emptyPage());
        toast.error('Error al cargar series liberadas.');
      } finally {
        setIsLoading(false);
      }
    },
    [http.izzi],
  );

  const onRelPageChange = (model: { page: number; pageSize: number }) => {
    setRelPage(model);
    fetchReleased(model.page, model.pageSize, appliedFilter);
  };

  // ── Tab 3: Errores ───────────────────────────────────────────────────────

  const fetchErrors = useCallback(async (page: number, pageSize: number) => {
    setIsLoading(true);
    try {
      const { data } = await httpPublic.get<{ data: SerialValidationRecord[]; total: number }>(
        '/api/v3/ops-izzi/serial-validations/errors',
        { params: { page, pageSize } },
      );
      setErrData({ rows: data.data, total: data.total });
    } catch {
      toast.error('Error al cargar la lista de errores.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onErrPageChange = (model: { page: number; pageSize: number }) => {
    setErrPage(model);
    fetchErrors(model.page, model.pageSize);
  };

  const onRevalidate = async (id: string) => {
    setIsLoading(true);
    try {
      const { data } = await httpPublic.post<ProcessInventoryResponse>(
        `/api/v3/ops-izzi/serial-validations/${id}/revalidate`,
      );
      if (data.status === 'REJECTED' || data.status === 'ERROR') {
        throw new Error(cleanMessage(data.message || 'El serial sigue con error.'));
      }
      toast.success('Serie revalidada correctamente.');
      fetchErrors(errPage.page, errPage.pageSize);
    } catch (error) {
      const axiosMsg = isAxiosError(error) ? error.response?.data?.message : null;
      const finalMsg =
        axiosMsg ||
        (error instanceof Error ? error.message : 'Error al revalidar.');
      toast.error(cleanMessage(finalMsg));
    } finally {
      setIsLoading(false);
    }
  };

  const onOpenModal = (msg: string) => {
    setSelectedError(msg);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setSelectedError(null);
  };

  // ── Navegación entre tabs ────────────────────────────────────────────────

  const onTabChange = (_: React.SyntheticEvent, value: SeriesTab) => {
    setActiveTab(value);
    setFilterInput('');
    setAppliedFilter('');
    if (value === 1) fetchQuarantined(0, DEFAULT_PAGE_SIZE);
    if (value === 2) fetchReleased(0, DEFAULT_PAGE_SIZE);
    if (value === 3) fetchErrors(0, DEFAULT_PAGE_SIZE);
  };

  // ── Búsqueda / filtro ────────────────────────────────────────────────────

  const onFilterChange = (value: string) => setFilterInput(value);

  const onSearch = () => {
    const po = filterInput.trim();
    setAppliedFilter(po);

    if (activeTab === 0) {
      if (!po) {
        toast.warning('Ingresa un número de PO para buscar.');
        return;
      }
      setPoPage((p) => ({ ...p, page: 0 }));
      fetchByPo(0, poPage.pageSize, po);
    } else if (activeTab === 1) {
      setQuarPage((p) => ({ ...p, page: 0 }));
      fetchQuarantined(0, quarPage.pageSize, po);
    } else if (activeTab === 2) {
      setRelPage((p) => ({ ...p, page: 0 }));
      fetchReleased(0, relPage.pageSize, po);
    }
  };

  const onClearFilter = () => {
    setFilterInput('');
    setAppliedFilter('');
    if (activeTab === 0) setPoData(emptyPage());
    if (activeTab === 1) fetchQuarantined(0, quarPage.pageSize);
    if (activeTab === 2) fetchReleased(0, relPage.pageSize);
  };

  const onRefresh = () => {
    if (activeTab === 0 && appliedFilter) fetchByPo(poPage.page, poPage.pageSize, appliedFilter);
    if (activeTab === 1) fetchQuarantined(quarPage.page, quarPage.pageSize, appliedFilter);
    if (activeTab === 2) fetchReleased(relPage.page, relPage.pageSize, appliedFilter);
    if (activeTab === 3) fetchErrors(errPage.page, errPage.pageSize);
  };

  return {
    activeTab,
    onTabChange,
    isLoading,
    filterInput,
    appliedFilter,
    onFilterChange,
    onSearch,
    onClearFilter,
    onRefresh,
    // Tab 0
    poData,
    poPage,
    onPoPageChange,
    onDelete,
    // Tab 1
    quarData,
    quarPage,
    onQuarPageChange,
    // Tab 2
    relData,
    relPage,
    onRelPageChange,
    // Tab 3
    errData,
    errPage,
    onErrPageChange,
    onRevalidate,
    isModalOpen,
    selectedError,
    onOpenModal,
    onCloseModal,
  };
};
